/**
 * Bulgarian catalogue.
 *
 * Terminology follows Bulgarian IP practice rather than a literal translation:
 * "опозиция" is the term the Patent Office uses for an opposition, "марка" for
 * a trademark, and "клиент" for the client company a firm represents. A test
 * asserts this file has exactly the same key set as `en.ts`.
 */
import monitoring from './bg/monitoring'
import trademarks from './bg/trademarks'
import general from './bg/general'
import people from './bg/people'
import explain from './bg/explain'

export default {
  nav: {
    workspace: 'Работна среда',
    workspaceTools: 'Инструменти',
    dashboard: 'Табло',
    monitoring: 'Наблюдение на марки',
    trademarks: 'Моите марки',
    oppositions: 'Опозиции',
    reports: 'Справки',
    clients: 'Клиенти',
    settings: 'Настройки',
  },

  topbar: {
    openMenu: 'Отвори менюто',
    closeMenu: 'Затвори менюто',
    search: 'Търсене на марки, притежатели, класове…',
    searchLabel: 'Търсене',
    openSearch: 'Отвори глобалното търсене',
    notifications: 'Известия',
    notificationsUnread: 'Известия, {n} непрочетени',
    help: 'Помощ',
    account: 'Профил',
  },

  account: {
    profile: 'Профил',
    settings: 'Настройки',
    security: 'Сигурност',
    help: 'Помощен център',
    signOut: 'Изход',
    language: 'Език',
    menuLabel: 'Профил',
    page: {
      title: 'Вашият профил',
      subtitle: 'Вашето име, език и парола.',
      loadFailed: 'Профилът ви не можа да бъде зареден.',
      profile: 'Профил',
      profileSub: 'Как ви виждат колегите и как се показвате в хронологията на делата.',
      name: 'Име и фамилия',
      email: 'Имейл за вход',
      emailHint: 'Адресът за вход се променя от кантората, не оттук.',
      tier: 'Вид профил',
      firm: 'Кантора',
      tiers: {
        firm_main: 'Основен профил на кантората',
        firm_manager: 'Мениджър',
        client_viewer: 'Клиентски профил (само за четене)',
      },
      save: 'Запази профила',
      saved: 'Профилът е запазен',
      failed: 'Профилът не беше запазен',
      nameRequired: 'Въведете името си.',
      password: 'Парола',
      passwordSub: 'За да я смените, първо въведете текущата си парола.',
      current: 'Текуща парола',
      new: 'Нова парола',
      repeat: 'Повторете новата парола',
      minLength: 'Поне {n} знака.',
      tooShort: 'Новата парола трябва да е поне {n} знака.',
      needCurrent: 'Въведете текущата си парола.',
      mismatch: 'Двете нови пароли не съвпадат.',
      change: 'Смени паролата',
      changed: 'Паролата е сменена',
      wrongCurrent: 'Текущата парола не е вярна.',
      changeFailed: 'Паролата не беше сменена',
      notYet:
        'Двуфакторно влизане, списък на устройствата с активен вход и API ключове все още ги няма в приложението.',
    },
  },

  auth: {
    title: 'Добре дошли отново',
    subtitle: 'Влезте, за да управлявате портфолиото си от марки.',
    email: 'Служебен имейл',
    emailPlaceholder: "vie{'@'}kantora.bg",
    password: 'Парола',
    remember: 'Запомни ме',
    forgot: 'Забравена парола?',
    signIn: 'Вход',
    signingIn: 'Влизане…',
    noAccount: 'Нямате акаунт?',
    requestAccess: 'Заявете достъп',
    welcomeBack: 'Добре дошли отново',
    failed: 'Неуспешен вход',
    checkCredentials: 'Проверете данните си и опитайте отново.',
    enterEmail: 'Въведете имейл',
    enterPassword: 'Въведете парола',
    reset: {
      title: 'Нова парола',
      sub: 'Въведете имейла, с който влизате. Ако има профил с него, ще му изпратим връзка за нова парола.',
      submit: 'Изпрати връзката',
      sending: 'Изпращане…',
      sent: 'Ако има профил с този адрес, връзката вече пътува към него.',
      sentSub:
        'Връзката работи веднъж, 30 минути. Ако нищо не пристигне, проверете папката за спам или поискайте отново.',
      back: 'Обратно към входа',
      failed: 'Заявката не можа да бъде изпратена. Опитайте отново след малко.',
    },
    link: {
      checking: 'Проверка на връзката…',
      invalid: 'Тази връзка вече не е валидна',
      invalidSub: 'Връзката работи веднъж и изтича. Поискайте нова по-долу.',
      askNew: 'Поискай нова връзка',
      unreachable: 'Връзката не можа да бъде проверена',
      resetTitle: 'Задайте нова парола за {email}',
      inviteTitle: 'Изберете парола за {email}',
      resetSub: 'Новата парола замества старата и влизате веднага.',
      inviteSub: 'Поканени сте в Lexentiqa. Изберете парола и влизате веднага.',
      expires: 'Връзката изтича {when}.',
      password: 'Нова парола',
      confirm: 'Повторете паролата',
      minLength: 'Поне {n} знака.',
      tooShort: 'Паролата трябва да е поне {n} знака.',
      mismatch: 'Двете пароли не съвпадат.',
      submit: 'Задай паролата и влез',
      submitting: 'Запазване…',
      done: 'Паролата е зададена — влязохте',
      failed: 'Паролата не беше зададена',
    },
  },

  common: {
    save: 'Запази',
    cancel: 'Отказ',
    confirm: 'Потвърди',
    delete: 'Изтрий',
    edit: 'Редактирай',
    close: 'Затвори',
    retry: 'Опитай отново',
    loading: 'Зареждане…',
    search: 'Търсене',
    clearAll: 'Изчисти всички',
    skipToContent: 'Към основното съдържание',
  },

  states: {
    emptyTitle: 'Все още няма нищо',
    emptyMessage: 'Няма резултати за текущите филтри.',
    errorTitle: 'Възникна грешка',
    errorMessage: 'Страницата не можа да бъде заредена.',
    notFoundTitle: 'Страницата не е намерена',
    offline: 'Изглежда сте офлайн.',
  },

  time: {
    dueToday: 'Краен срок днес',
    daysLeft: 'остава {n} ден | остават {n} дни',
    daysOverdue: 'просрочено с {n} ден | просрочено с {n} дни',
  },

  /* Камбанката и страницата с известия (W11). */
  notif: {
    subtitle:
      'Какво се е случило по делата ви и в наблюдението, най-новото първо. Всяко известие отваря това, за което се отнася.',
    popupNew: '{n} нови',
    popupEmpty: 'Все още няма нищо.',
    viewAll: 'Всички известия',
    markAllRead: 'Маркирай всички като прочетени',
    markAllTitle: 'Да се маркират ли всички известия като прочетени?',
    markAllBody: 'Отнася се за всички, не само за тези на тази страница.',
    markAllDone: 'Всички са маркирани като прочетени',
    settingsLink: 'Настройки на известията',
    failed: 'Промяната не беше запазена',
    loadFailed: 'Известията не можаха да бъдат заредени.',
    count: '{n} известие | {n} известия',
    unreadCount: '{n} непрочетени',
    unread: 'непрочетено',
    markRead: 'Маркирай като прочетено',
    remove: 'Изтрий известието',
    range: '{from}–{to} от {total}',
    prev: 'Предишна страница',
    next: 'Следваща страница',
    tabs: {
      all: 'Всички',
      unread: 'Непрочетени',
      match_found: 'Съвпадения',
      deadline_approaching: 'Срокове',
      procedure_update: 'Промени по дела',
      system: 'Съобщения',
    },
    empty: {
      allTitle: 'Все още няма известия',
      allMessage:
        'Когато наблюдението открие съвпадение с висок риск, наближи срок по дело или се промени дело, по което работите, това ще се появи тук.',
      unreadTitle: 'Всичко е прочетено',
      unreadMessage: 'Няма нищо непрочетено.',
      kindTitle: 'Няма известия от този вид',
      kindMessage: 'Не са ви изпращани известия от този вид.',
    },
  },

  prefs: {
    title: 'Настройки за известия',
    sub: 'За всеки вид събитие: дали да ви достига в приложението, по имейл или и по двата начина.',
    event: 'Събитие',
    channel: {
      in_app: 'В приложението',
      email: 'По имейл',
    },
    kind: {
      match_found: {
        title: 'Съвпадения с висок риск',
        sub: 'Нови съвпадения с висок риск за марките, които виждате — едно обобщение на сканиране, а не по едно за всяко съвпадение.',
      },
      deadline_approaching: {
        title: 'Срокове по дела',
        sub: 'Седмица преди това, ден преди това, в самия ден и след изтичането — за делата, възложени на вас.',
      },
      procedure_update: {
        title: 'Промени по дела',
        sub: 'Смяна на статус, бележки, документи и срокове по делата, възложени на вас — никога вашите собствени действия.',
        viewer: 'Промените, които кантората споделя с вас по вашите дела.',
      },
      system: {
        title: 'Съобщения',
        sub: 'Съобщения за самата платформа, например планирана поддръжка.',
      },
    },
    defaultNote: 'Превключвател, който никога не сте променяли, е включен.',
    emailNote:
      'Дали изобщо се изпращат имейли, решава операторът на платформата за цялата платформа; тези превключватели решават само дали вие ще ги получавате.',
    failed: 'Настройката за известия не беше запазена',
    loadFailed: 'Настройките за известия не можаха да бъдат заредени.',
  },

  reports: {
    title: 'Отчети от сканиране',
    subtitle:
      'По един отчет за всеки ден, в който наблюдението е сканирало марките ви: какво е сравнило и какво е открило.',
    dayCount: '{n} ден с отчет | {n} дни с отчет',
    newestFirst: 'Най-новите първо',
    loadFailed: 'Отчетите не можаха да бъдат заредени.',
    emptyTitle: 'Все още няма сканирания',
    emptyMessage: 'Когато наблюдението сканира марките ви, всеки такъв ден ще има отчет тук.',
    col: {
      day: 'Ден',
      brands: 'Проверени марки',
      compared: 'Сравнени заявки',
      matches: 'Открити съвпадения',
      high: 'Висок риск',
      judged: 'Прочетени от AI съдията',
    },
    noScan: 'Няма записано сканиране',
    open: 'Отвори отчета за {day}',
    pdfFor: 'Изтегли отчета за {day} като PDF файл',
    htmlFor: 'Изтегли отчета за {day} като страница за печат',
    range: '{from}–{to} от {total}',
    prev: 'Предишна страница',
    next: 'Следваща страница',
    note: 'Сравнените заявки са двойките, които системата е оценила спрямо марките ви; съвпаденията са двойките, които е задържала. Ден със съвпадения, но без записано сканиране, е от времето преди сканиранията да се записват.',
    pdf: 'Изтегли PDF',
    html: 'Страница за печат',
    pdfUnavailable: 'В момента сървърът не може да създава PDF файлове',
    pdfUnavailableSub:
      'Страницата за печат има същото съдържание: изтеглете я и я отпечатайте от браузъра.',
    pdfUnavailableCase: 'Операторът на платформата може да включи създаването на PDF на сървъра.',
    downloadFailed: 'Изтеглянето не успя',
    state: {
      new: 'Ново',
      reviewed: 'Прегледано',
      dismissed: 'Отхвърлено',
      escalated: 'Открито е дело',
    },
    detail: {
      title: 'Отчет от сканиране · {day}',
      noRun: 'За този ден няма записано сканиране; записани са съвпаденията по-долу.',
      ran: 'Изпълнено {from}–{to} UTC',
      runs: '{n} сканиране | {n} сканирания',
      totals: 'Денят в числа',
      comparedHint: 'Оценени двойки кандидати',
      judgedHint: 'Останалите са решени от правила за съвпадение',
      matchesTitle: 'Открити съвпадения',
      matchesSub: 'Всяко съвпадение, записано в този ден, което можете да видите.',
      noMatches: 'В този ден не е записано съвпадение.',
      brandsTitle: 'Проверени марки',
      brandsSub:
        'Всяка марка, сравнена при сканиранията през деня, първо тези с най-много съвпадения с висок риск.',
      noBrands: 'В този ден не е сканирана марка.',
      col: {
        trademark: 'Марка',
        found: 'Открита',
        registry: 'Регистър',
        risk: 'Риск',
        decidedBy: 'Решено от',
        state: 'Преглед',
        deadline: 'Срок за опозиция',
        client: 'Клиент',
        registries: 'Регистри',
      },
      judge: 'AI съдията',
      rules: 'Правило за съвпадение',
      showAll: 'Покажи всички {n}',
      showFewer: 'Покажи по-малко',
      truncated: 'Показани са първите {shown} от {total} съвпадения.',
      back: 'Обратно към отчетите',
      loadFailed: 'Отчетът не можа да бъде зареден.',
    },
  },

  settings: {
    title: 'Настройки',
    subtitle: 'Вашият език и известия и връзките на кантората с регистрите.',
    subtitleViewer: 'Вашият език и вашите известия.',
    sections: 'Раздели на настройките',
    mainOnly: 'Само основният профил на кантората може да променя това.',
    nav: {
      language: 'Език',
      notifications: 'Известия',
      firm: 'Кантора',
      registries: 'Национални регистри',
      people: 'Хора',
      later: 'Все още не е в приложението',
    },
    language: {
      title: 'Език',
      sub: 'Езикът на приложението и на имейлите и известията, които получавате.',
      emails: 'Имейлите и известията ви се пишат на {language}.',
      mismatch: 'Имейлите ви все още се пишат на {stored}.',
      useCurrent: 'Пишете ги и тях на {language}',
      failed:
        'Езикът е сменен тук, но не е запазен в профила ви, затова имейлите остават на досегашния език.',
    },
    firm: {
      title: 'Кантора',
      sub: 'Кантората ви така, както се показва в отпечатаните отчети.',
      name: 'Име на кантората',
      email: 'Имейл за контакт',
      save: 'Запази данните на кантората',
      saved: 'Данните на кантората са запазени',
      failed: 'Данните на кантората не бяха запазени',
      nameRequired: 'Въведете името на кантората.',
      loadFailed: 'Данните на кантората не можаха да бъдат заредени.',
    },
    registries: {
      title: 'Национални регистри',
      sub: 'Как платформата достига до националното ведомство по марки на дадена държава за националния обхват на наблюдение.',
      credentials:
        'Пароли и ключове никога не се въвеждат тук. Операторът на платформата ги настройва на сървъра.',
      add: 'Добави регистър',
      country: 'Код на държавата',
      countryHint: 'Две букви, например BG.',
      type: 'Връзка',
      kind: {
        api: 'Уеб API',
        ftp: 'FTP сървър',
      },
      address: 'Адрес',
      addressHint:
        'Адресът на сървъра или уеб адресът на регистъра — без потребителско име и парола.',
      useIt: 'Използвай тази връзка',
      save: 'Запази връзката',
      badCountry: 'Въведете двубуквен код на държава.',
      exists: 'Тази държава вече е свързана; редактирайте връзката.',
      saved: 'Връзката за {country} е запазена',
      failed: 'Връзката не беше запазена',
      remove: 'Премахни',
      removeTitle: 'Да се премахне ли връзката за {country}?',
      removeBody:
        'Платформата спира да я използва. Данните за достъп при оператора на платформата не се засягат.',
      removed: 'Връзката е премахната',
      loadFailed: 'Връзките с регистрите не можаха да бъдат заредени.',
      empty: 'Няма свързан национален регистър',
      emptyMessage:
        'Националният обхват следи националното ведомство по марки на държавата. Връзката записва как да се стигне до него.',
      active: 'Използва се',
      paused: 'Не се използва',
      credOnFile: 'Данни за достъп: настроени от оператора на платформата.',
      credMissing:
        'Данни за достъп: още не са настроени — обърнете се към оператора на платформата.',
      adapterYes: 'Платформата може да чете този регистър.',
      adapterNo:
        'Платформата все още не може да чете този регистър. Връзката е записана; национално сканиране няма да се изпълнява, докато това не стане възможно.',
      other: 'Записано още: {keys}',
      updated: 'Обновено {date}',
    },
    people: {
      title: 'Хора',
      sub: 'Колеги и хора от клиентите, които могат да влизат, и какво вижда всеки от тях.',
      open: 'Управление на хората',
      mainOnly: 'Само основният профил на кантората добавя хора и избира какво виждат.',
    },
    later: {
      title: 'Все още не е в приложението',
      sub: 'Често питано, но тази страница все още не може да го направи.',
      signinTitle: 'Сигурност при влизане',
      signin: 'Двуфакторно влизане и единен вход все още не са налични.',
      matchingTitle: 'Как се оценяват конфликтите',
      matching:
        'Системата за съвпадения се настройва от оператора за цялата платформа, а не поотделно за всяка кантора.',
    },
  },

  /* Дела по опозиция (W10). „Опозиция" е терминът на Патентното ведомство;
   * кодовете на регистрите (EUIPO, WIPO) се печатат от данните, не оттук. */
  opp: {
    title: 'Дела по опозиция',
    caseRef: 'Дело {ref}',
    registryBg: 'Патентно ведомство на България',
    status: {
      under_review: 'В преглед',
      drafting: 'Изготвяне',
      filed: 'Подадена опозиция',
      awaiting_response: 'Очаква отговор',
      negotiation: 'Преговори',
      resolved: 'Решено',
      closed: 'Приключено',
    },
    outcome: {
      won: 'Спечелено',
      settled: 'Споразумение',
      lost: 'Загубено',
      withdrawn: 'Оттеглено',
    },
    priority: {
      urgent: 'Спешен',
      high: 'Висок',
      medium: 'Среден',
      low: 'Нисък',
    },
    risk: {
      high: 'Висок риск',
      med: 'Среден риск',
      low: 'Нисък риск',
      none: 'Делото няма съвпадение зад себе си, затова няма оценка на риска',
    },
    verdict: {
      conflict: 'Конфликт',
      strong_match: 'Конфликт',
      unclear: 'Нужно е човешко решение',
      review: 'Нужно е човешко решение',
      no_conflict: 'Няма конфликт',
      dismiss: 'Няма конфликт',
    },
    kind: {
      statutory: 'Законов',
      internal: 'Вътрешен',
    },
    noteCategory: {
      legal: 'Правна',
      internal: 'Вътрешна',
      status: 'Промяна на статуса',
    },
    docCategory: {
      filing: 'Подаване',
      evidence: 'Доказателство',
      correspondence: 'Кореспонденция',
      decision: 'Решение',
      agreement: 'Споразумение',
      other: 'Друго',
    },
    deadline: {
      provisional: 'Предварителен: правилото се преглежда',
      provisionalShort: 'предварителен',
      provisionalHelp:
        'Датата следва правило на регистъра, което юристите на кантората още не са потвърдили. Проверете я, преди да разчитате на нея.',
      none: 'Няма открит срок',
      finished: 'Делото е приключило',
      done: 'Изпълнен {date}',
      fromMark: 'Взет от публикацията на регистъра',
      fromRule: 'Определен от правило за срокове',
      fromFirm: 'Добавен от кантората',
    },
    list: {
      subtitle:
        'Всеки конфликт, по който кантората е открила досие: статусът му, следващият срок и кой работи по него.',
      filters: 'Филтри',
      create: 'Ново дело по опозиция',
      cases: '{n} дело | {n} дела',
      filtersOn: '{n} активен филтър | {n} активни филтъра',
      search: 'Търсене по заглавие, номер на дело, марка или клиент…',
      searchLabel: 'Търсене на дела',
      range: 'Показани {from}–{to} от {total}',
      prev: 'Предишна страница',
      next: 'Следваща страница',
      emptyTitle: 'Все още няма дела по опозиция',
      emptyMessage:
        'Дело се открива от съвпадение на страницата за наблюдение или ръчно от тази страница.',
      toMonitoring: 'Към наблюдението',
      noMatchTitle: 'Няма дела, отговарящи на филтрите',
      noMatchMessage: 'Изчистете филтрите, за да видите всички дела.',
      clearFilters: 'Изчисти филтрите',
      loadFailed: 'Делата не можаха да бъдат заредени.',
    },
    kpi: {
      active: 'Активни дела',
      openedThisMonth: '{n} открити този месец',
      urgent: 'Просрочени или със срок до 14 дни',
      overdue: '{n} просрочени',
      due14: '{n} със срок до 14 дни',
      awaiting: 'Очакват отговор',
      awaitingHint: 'Подадени и в очакване на другата страна',
      won: 'Спечелени или със споразумение тази година',
      wonHint: 'от {n} решени дела тази година',
    },
    filter: {
      registry: 'Регистър',
      status: 'Статус',
      deadline: 'Следващ срок',
      deadlineNote: 'Броят се само активните дела.',
      overdue: 'Просрочени',
      within: 'До {n} дни',
      any: 'Всяка дата',
      priority: 'Приоритет',
      activeOnly: 'Само активни дела',
      client: 'Клиент: {name}',
      brand: 'Марка: {name}',
      search: 'Търсене: {q}',
    },
    sort: {
      label: 'Подредба',
      deadline: 'Първо най-близкият срок',
      recent: 'Последно обновени',
      priority: 'Първо най-висок приоритет',
      created: 'Първо най-новите дела',
    },
    col: {
      conflict: 'Конфликт на марки',
      risk: 'Риск от съвпадението',
      status: 'Статус',
      deadline: 'Следващ срок',
      lawyer: 'Юрист',
    },
    preview: {
      label: 'Преглед на делото',
      show: 'Покажи в прегледа',
      close: 'Затвори прегледа',
      origin: 'Откъде идва делото',
      judged: 'Прочетено от AI съдията',
      rules: 'Открито от правило за съвпадение',
      byHand: 'Открито ръчно. Зад него няма съвпадение, затова няма оценка на риска.',
      viewMatch: 'Вижте как е открито съвпадението',
      nextDeadline: 'Следващ срок',
      open: 'Отвори досието',
    },
    fact: {
      lawyer: 'Отговорен юрист',
      unassigned: 'Неразпределено',
      client: 'Клиент',
      documents: 'Документи',
      files: '{n} файл | {n} файла',
      notes: 'Бележки',
      noteCount: '{n} бележка | {n} бележки',
      lastActivity: 'Последна дейност',
      grounds: 'Основания',
      notRecorded: 'Още не са въведени',
      caseNumber: 'Номер на делото',
      priority: 'Приоритет',
      registry: 'Регистър',
      filed: 'Дата на подаване',
      createdBy: 'Открито от',
      internalNotes: 'Работни бележки',
    },
    activity: {
      title: 'Последна правна дейност',
      sub: 'Най-новите промени по всички дела, които виждате.',
      empty: 'Все още няма дейност по нито едно дело.',
    },
    event: {
      created: 'Делото е открито ръчно',
      createdFromMatch: 'Делото е открито от съвпадение при наблюдението',
      status: 'Статусът е сменен от „{from}“ на „{to}“',
      statusOutcome: 'Статусът е сменен от „{from}“ на „{to}“: {outcome}',
      note: 'Добавена бележка ({category})',
      documentAdded: 'Добавен документ: {name}',
      documentRemoved: 'Премахнат документ: {name}',
      documentUpdated: 'Променени данни на документ: {name}',
      assigned: 'Възложено на {name}',
      assignedSomeone: 'Сменен е отговорният юрист',
      unassigned: 'Отговорният юрист е премахнат',
      deadlineAdded: 'Добавен срок: {title}, до {date}',
      deadlineDone: 'Изпълнен срок: {title}',
      deadlineReopened: 'Срокът е отворен отново: {title}',
      deadlineEdited: 'Променен срок: {title}',
      deadlineRemoved: 'Премахнат срок: {title}',
      priority: 'Приоритетът е сменен от „{from}“ на „{to}“',
      edited: 'Редактирани данни на делото',
      windowClosed:
        'Срокът за опозиция срещу тази марка е изтекъл на {date}, преди делото да бъде открито',
      other: 'Делото е обновено',
      someone: 'Някой',
      clientVisible: 'Видимо за клиента',
    },
    create: {
      crumb: 'Ново дело',
      title: 'Откриване на дело по опозиция',
      subtitle: 'Делото е досието на един конфликт: марката на клиента срещу една заявка.',
      submit: 'Открий делото',
      submitting: 'Откриване…',
      draftFailed:
        'Съвпадението не можа да бъде заредено. Възможно е да е премахнато или да нямате достъп до него.',
      existingTitle: 'Това съвпадение вече има открито дело',
      existingBody:
        'Едно съвпадение може да има само едно открито дело. Продължете в съществуващото.',
      openExisting: 'Отвори съществуващото дело',
      conflictSection: 'Конфликтът',
      conflictFromMatch:
        'Взето от съвпадението. Данните, които определят конфликта, не могат да се променят тук.',
      analysisSection: 'Какво откри системата',
      judgeScore: 'Вероятността за объркване според AI съдията',
      ruleScore: 'Сходство от правило за съвпадение, не вероятност',
      noJustification: 'Двойката е открита от правило; никой модел не е написал обосновка.',
      pickSection: 'Марката на клиента',
      client: 'Клиент',
      clientSearch: 'Търсене на клиенти…',
      chooseClient: 'Изберете клиент',
      brand: 'Марка',
      brandSearch: 'Търсене в марките на клиента…',
      chooseBrand: 'Изберете марка',
      noBrands: 'Този клиент още няма марки.',
      narrow: 'Показани {shown} от {total}. Пишете, за да стесните списъка.',
      opposedSection: 'Оспорваната марка',
      markName: 'Наименование на марката',
      markNumber: 'Номер на заявката',
      markOwner: 'Заявител или притежател',
      registry: 'Регистър',
      registryNone: 'Не е избран',
      handNote:
        'Зад ръчно открито дело няма съвпадение, затова няма оценка на риска и срок от регистъра. Добавете срока на страницата на делото.',
      caseSection: 'Делото',
      caseTitle: 'Заглавие на делото',
      caseTitleHint: 'Ако остане празно, делото ще се казва „{title}“.',
      caseNumber: 'Номер на делото',
      caseNumberHint: 'Номерът, даден от ведомството, когато има такъв.',
      priority: 'Приоритет',
      startAs: 'Начален статус',
      lawyer: 'Отговорен юрист',
      grounds: 'Основания',
      groundsPlaceholder: 'напр. чл. 8(1)(б) РМЕС',
      notes: 'Вътрешни бележки',
      notesHint: 'Работните бележки на кантората. Никога не се показват на клиента.',
      markDeadline: 'Срокът за опозиция на откритата марка, {date}, става първият срок на делото.',
      markDeadlinePassed:
        'Срокът за опозиция срещу тази марка е изтекъл на {date}. Делото ще запише това, вместо да носи просрочен срок.',
      afterNote:
        'Документи, срокове и бележки се добавят на страницата на делото, след като бъде открито.',
      pickBrand: 'Изберете марката на клиента.',
      nameMark: 'Въведете оспорваната марка.',
      failed: 'Делото не беше открито',
      created: 'Делото е открито',
      lawyersFailed:
        'Списъкът с юристи не можа да бъде зареден. Делото може да бъде възложено по-късно.',
    },
    detail: {
      assigned: 'Възложено на {name}',
      filed: 'Подадено на {date}',
      backToCases: 'Обратно към делата',
      addNote: 'Добави бележка',
      upload: 'Качи документ',
      uploading: 'Качване…',
      updateStatus: 'Смени статуса',
      delete: 'Изтрий делото',
      deleteTitle: 'Да се изтрие ли делото?',
      deleteBody:
        'Делото, хронологията, бележките и файловете му се премахват завинаги. За да спрете работа, но да запазите историята, го приключете.',
      deleted: 'Делото е изтрито',
      deleteFailed: 'Делото не беше изтрито',
      readOnly: 'Можете да четете това дело, но не и да го променяте.',
      sections: 'Раздели на делото',
      tabConflict: 'Конфликт',
      tabAnalysis: 'Анализ',
      tabNotes: 'Бележки',
      tabDocs: 'Документи',
      tabTimeline: 'Хронология',
      protected: 'Марката на клиента',
      opposed: 'Оспорвана марка',
      yourClient: 'Ваш клиент',
      opposedTag: 'Оспорвана',
      applicationNo: 'Номер на заявката',
      registered: 'Регистрирана',
      filedOn: 'Подадена',
      classes: 'Класове по Ница',
      type: 'Вид',
      owner: 'Притежател',
      windowCloses: 'Срок за опозиция',
      handTyped: 'Въведено ръчно: известно е само това, което юристът е въвел.',
      analysisJudge: 'Прочетено от AI съдията. Решението и обосновката му са по-долу.',
      analysisRules:
        'Открито от правило за съвпадение. Никой модел не е прочел двойката, а числото е сходство, не вероятност.',
      score: '{n} от 100',
      verdict: 'Решение',
      overlap: 'Общи класове по Ница',
      noOverlap: 'Няма общ клас по Ница',
      onlyTheirs: 'само техен',
      loadFailed: 'Част от делото не можа да бъде заредена.',
      editDetails: 'Редактирай',
      saveDetails: 'Запази данните',
      detailsSaved: 'Данните на делото са запазени',
      detailsFailed: 'Данните на делото не бяха запазени',
      summaryPdf: 'Резюме на делото (PDF)',
    },
    notes: {
      title: 'Бележки по делото',
      sub: 'Правни констатации, вътрешни работни бележки и промени на статуса.',
      all: 'Всички',
      placeholder: 'Напишете правна бележка, констатация или указание към екипа…',
      visible: 'Видимо за клиента',
      visibleHint: 'Профилът на клиента ще вижда тази бележка.',
      post: 'Публикувай',
      posting: 'Публикуване…',
      empty: 'Още няма бележки.',
      emptyCategory: 'Няма бележки в тази категория.',
      added: 'Бележката е добавена',
      failed: 'Бележката не беше запазена',
    },
    docs: {
      title: 'Документи',
      sub: 'Подадени документи, доказателства, кореспонденция и решения.',
      category: 'Категория',
      choose: 'Качи файл',
      hint: 'PDF, Word, текстов файл или изображение.',
      visibleShort: 'Клиентът вижда',
      hiddenShort: 'Само за кантората',
      showToClient: 'Покажи на клиента',
      hideFromClient: 'Скрий от клиента',
      download: 'Изтегли {name}',
      delete: 'Изтрий {name}',
      deleteTitle: 'Да се изтрие ли {name}?',
      deleteBody: 'Файлът се премахва от делото. Това не може да бъде отменено.',
      empty: 'Няма документи по делото.',
      uploaded: 'Качен: {name}',
      failed: 'Качването не успя',
      actionFailed: 'Промяната по документа не беше запазена',
    },
    timeline: {
      title: 'Хронология на делото',
      sub: 'Всяка промяна по делото, най-новите първи. Историята не може да се редактира.',
      empty: 'Още няма записи.',
    },
    lifecycle: {
      title: 'Статус на делото',
      moveTo: 'Премести в',
      choose: 'Изберете следващата стъпка',
      outcome: 'Как приключи?',
      chooseOutcome: 'Изберете резултат',
      reason: 'Защо делото се приключва?',
      reopenReason: 'Защо се отваря отново? (по избор)',
      filingDate: 'Дата на подаване',
      clientVisible: 'Покажи тази стъпка на клиента',
      apply: 'Смени статуса',
      applying: 'Запазване…',
      needOutcome: 'Посочете как приключи делото.',
      needReason: 'Посочете защо делото се приключва.',
      changed: 'Статусът е сменен на „{status}“',
      failed: 'Статусът не беше сменен',
      noMoves: 'Няма следващи стъпки оттук.',
      outcomeLine: 'Резултат: {outcome}',
    },
    deadlines: {
      title: 'Срокове',
      empty: 'Няма срокове по делото.',
      markDone: 'Отбележи като изпълнен',
      reopen: 'Отвори отново',
      delete: 'Изтрий срока',
      deleteTitle: 'Да се изтрие ли „{title}“?',
      deleteBody: 'Срокът се премахва. Хронологията пази запис, че е съществувал.',
      add: 'Добави срок',
      adding: 'Добавяне…',
      newTitle: 'Какво предстои',
      newDate: 'Краен срок',
      newKind: 'Вид',
      newNote: 'Бележка (по избор)',
      failed: 'Срокът не беше запазен',
    },
    related: {
      title: 'Дела по опозиция',
      count: '{n} дело | {n} дела',
      none: 'Още няма дела по опозиция.',
      viewAll: 'Виж всички',
      create: 'Ново дело',
      failed: 'Делата не можаха да бъдат заредени.',
    },
    match: {
      openCase: 'Открий дело по опозиция',
      openCaseHint:
        'Открива дело, попълнено от това съвпадение. Едно съвпадение може да има само едно открито дело.',
    },
  },

  clients: {
    title: 'Клиенти и притежатели на марки',
    subtitle: 'Дружествата, чиито марки кантората защитава: техните марки, наблюдение и дела.',
    filters: 'Филтри',
    add: 'Добави клиент',
    unit: 'клиента',
    shown: '{n} от {total} клиента',
    filtersActive: '{n} активен филтър | {n} активни филтъра',
    searchPlaceholder: 'Търсене на клиенти…',
    unassigned: 'Без отговорник',
    kpi: {
      label: 'Преглед на клиентите',
      total: 'Клиенти',
      newThisQuarter: 'нови това тримесечие',
      trademarks: 'Марки',
      perClient: 'средно {n} на клиент',
      highRisk: 'Клиенти с висок риск',
      highRiskHint: 'Две или повече открити дела',
      registries: 'Регистри',
      registriesHint: 'Където се наблюдават марките им',
    },
    filter: {
      name: 'Име на клиента',
      namePlaceholder: 'Дружество или лице за контакт',
      registry: 'Регистър',
      monitoring: 'Наблюдение',
      risk: 'Ниво на риска',
    },
    chip: {
      risk: 'Риск: {level}',
      search: 'Търсене: {q}',
    },
    risk: {
      high: 'Висок',
      med: 'Среден',
      low: 'Нисък',
      none: 'Неоценен',
    },
    monitoring: {
      full: 'Изцяло наблюдаван',
      partial: 'Частично наблюдаван',
      paused: 'Наблюдението е спряно',
      none: 'Не се наблюдава',
      unknown: 'Наблюдението е неизвестно',
    },
    sort: {
      label: 'Подреди клиентите',
      name: 'Име, от А до Я',
      nameDesc: 'Име, от Я до А',
      trademarks: 'Най-много марки',
      cases: 'Най-много открити дела',
      recent: 'Скоро променени',
    },
    empty: {
      title: 'Няма клиенти, отговарящи на търсенето',
      message: 'Опитайте с друго търсене или изчистете активните филтри.',
      reset: 'Изчисти филтрите',
    },
    col: {
      client: 'Клиент',
      registries: 'Регистри',
      risk: 'Риск',
      lawyer: 'Юрист',
      trademarks: 'Марки',
    },
    preview: {
      label: 'Преглед на клиента',
      close: 'Затвори прегледа',
      summary: 'Обобщение',
      registries: '{n} регистър | {n} регистъра',
      monitoring: 'Наблюдавани',
      openCases: 'Открити дела',
      allMarks: 'Всички {n}',
      noMarks: 'Няма марки в досието.',
      registered: 'Регистрирана',
      pending: 'Висяща',
      classes: 'Клас {list} | Класове {list}',
      lawyers: 'Юристи',
      activity: 'Последни действия по делата',
      noActivity: 'Още няма действия по делата.',
      open: 'Отвори досието на клиента',
    },
    activity: {
      title: 'Последни действия по делата',
      sub: 'Най-новите записи от хронологията на всички дела, за всички клиенти.',
      empty: 'Още няма действия по делата.',
    },
    quick: {
      title: 'Бързи действия',
      addClient: 'Добавете дружество, чиито марки кантората защитава.',
      addTrademark: 'Добави марка',
      addTrademarkSub: 'Добавете марка към портфолиото на клиент и изберете къде да се наблюдава.',
      monitoring: 'Отвори наблюдението',
      monitoringSub: 'Прегледайте какво е открило наблюдението срещу марките на клиентите ви.',
      case: 'Открий дело',
      caseSub: 'Започнете дело по опозиция за клиент.',
    },
  },

  client: {
    back: 'Обратно към клиентите',
    edit: 'Редактиране',
    eyebrow: 'Клиент · {id}',
    riskClient: 'Риск: {level}',
    trademarkCount: '{n} марка | {n} марки',
    openCaseCount: '{n} открито дело | {n} открити дела',
    contact: 'Лице за контакт: {name}',
    monitoring: 'Наблюдение',
    watchedOf: '{n} от {total} марки се наблюдават',
    openMonitoring: 'Отвори наблюдението',
    newCase: 'Ново дело',
    sections: 'Раздели на досието',
    failed: 'Това не можа да бъде заредено. Презаредете страницата, за да опитате отново.',
    notWatched: 'Не се наблюдава',
    classes: 'Клас {list} | Класове {list}',
    registeredOn: 'Регистрирана на {date}',
    kpi: {
      label: 'Преглед на клиента',
      coverage: 'Обхват на наблюдението',
      casesOpen: 'Откритите дела чакат отговор',
      casesNone: 'Няма открити дела',
    },
    tab: {
      portfolio: 'Портфолио',
      cases: 'Дела',
      people: 'Хора',
      matches: 'Съвпадения',
      activity: 'Действия',
      monitoring: 'Наблюдение',
      documents: 'Документи',
    },
    portfolio: {
      title: 'Портфолио от марки',
      sub: 'Всички марки на клиента в досието, регистрирани или висящи.',
      all: 'Виж всички в „Моите марки“',
      emptyTitle: 'Още няма марки',
      empty: 'Клиентът няма марки в досието.',
    },
    people: {
      title: 'Хора с достъп',
      sub: 'Юристите, които работят по клиента, и собствените потребители на клиента. Основните профили на кантората виждат всички клиенти и не са изброени.',
      empty: 'Още никой не е получил достъп до този клиент.',
      add: 'Дайте достъп',
      manager: 'Юрист',
      viewer: 'Потребител на клиента',
      off: 'Изключен',
      wholeCompany: 'Цялото дружество',
      someMarks: '{n} марка | {n} марки',
    },
    matches: {
      title: 'Открити съвпадения',
      sub: 'Какво е открило наблюдението срещу марките на клиента, първо тези с изтичащ срок.',
      lastScan: 'Последна проверка {date}',
      awaiting: 'чакат преглед',
      high: 'с висок риск',
      closingSoon: 'с висок риск и срок до 14 дни',
      stillOpen: 'с още отворен срок',
      none: 'Няма съвпадение с още отворен срок за опозиция.',
      against: 'срещу {brand}',
    },
    activity: {
      title: 'Действия по делата',
      sub: 'Най-новите записи от делата по опозиция на клиента.',
      empty: 'Още няма действия по делата на този клиент.',
    },
    coverage: {
      title: 'Наблюдение по регистри',
      sub: 'Колко от марките на клиента се наблюдават във всеки регистър.',
      ofMarks: 'от {n} марка | от {n} марки',
      lastScan: 'Последна проверка {date}',
      waiting: 'Чака първата си проверка',
      notWatched: 'Нито една не се наблюдава тук',
    },
    docs: {
      title: 'Документи',
      sub: 'Файловете към марките на клиента. Добавят се от страницата на самата марка.',
      empty: 'Няма документи към марките на клиента.',
      open: 'Отвори {name}',
      openFailed: 'Файлът не можа да бъде отворен',
      kind: {
        logo: 'Лого',
        certificate: 'Свидетелство',
        evidence: 'Доказателство',
        correspondence: 'Кореспонденция',
        other: 'Друго',
      },
    },
  },

  /* The pages translated in the Wave 3 pass (2026-09-30), one file per area
   * so they could be written side by side. */
  ...monitoring,
  ...trademarks,
  ...general,
  ...people,
  ...explain,
} as const
